import type { ClientDto } from '@fos/shared/contracts/crm';
import { isValidNip } from '@fos/shared/nip';
import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  Clock,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Settings,
  UserRoundSearch,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { plural } from '../../../lib/format';
import { useRememberRecent } from '../../../lib/recent';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../ui/Menu';
import { Page } from '../../../ui/Page';
import { Breadcrumbs, PageHeader } from '../../../ui/PageHeader';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { TabPanel, Tabs } from '../../../ui/Tabs';
import { TOUCH_BUTTON } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { clientStatusLabel, formatClientRate } from '../client-format';
import { ArchiveClientDialog } from '../components/ArchiveClientDialog';
import { ClientFormDialog } from '../components/ClientFormDialog';
import { ClientOverview } from '../components/ClientOverview';
import { RegistryRefreshDialog } from '../components/RegistryRefreshDialog';
import {
  CLIENT_TABS,
  type ClientTabKey,
  ClientTabPlaceholder,
  getClientAddTimeDialog,
  getClientNewTaskDialog,
  getClientTab,
} from '../slots';

/** Linia meta (Figma: „Aktywny · 150 zł/h netto · Anna Kowalczyk, anna@rea.pl”; mobile krócej). */
function metaParts(client: ClientDto, compact: boolean): string[] {
  const parts = [clientStatusLabel(client.status)];
  if (client.defaultRate) {
    parts.push(`${formatClientRate(client.defaultRate, client.currency)}${compact ? '' : ' netto'}`);
  }
  const contact = client.primaryContact;
  if (contact) parts.push(!compact && contact.email ? `${contact.name}, ${contact.email}` : contact.name);
  return parts;
}

/** Profil klienta: karta + zakładki. Bez modułu Klienci: komunikat modułu. */
export function ClientProfilePage() {
  const access = useAccess();
  if (!access.can('clients')) return <ModulePage module="clients" />;
  return <ClientProfile />;
}

function ClientProfile() {
  const { id = '' } = useParams();
  const query = trpc.clients.get.useQuery(
    { id },
    { enabled: Boolean(id), retry: (n, err) => n < 2 && appErrorCode(err) !== 'NOT_FOUND' },
  );

  if (query.isPending) return <ProfileSkeleton />;
  if (query.isError) {
    const code = appErrorCode(query.error);
    return (
      <Page>
        <BackLink />
        {code === 'NOT_FOUND' || code === 'VALIDATION_ERROR' ? (
          <EmptyState
            icon={UserRoundSearch}
            title="Nie znaleziono klienta"
            description="Klient nie istnieje albo nie masz do niego dostępu."
            action={
              <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                <Link to="/klienci">Wróć do klientów</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
        )}
      </Page>
    );
  }
  return <ClientProfileView client={query.data} />;
}

/** Desktop: okruszki „Klienci”; mobile: „‹ Klienci” (+ akcje po prawej). */
function BackLink({ mobileAction }: { mobileAction?: ReactNode }) {
  return (
    <>
      <Breadcrumbs items={[{ label: 'Klienci', to: '/klienci' }]} className="mb-2 hidden md:flex" />
      <div className="mb-3 flex items-center justify-between md:hidden">
        <Link
          to="/klienci"
          className="inline-flex min-h-11 items-center gap-1 rounded-xs text-body-m text-fg focus-visible:outline-2 focus-visible:outline-accent"
        >
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden />
          Klienci
        </Link>
        {mobileAction}
      </div>
    </>
  );
}

function ProfileSkeleton() {
  return (
    <Page>
      <div role="status" aria-label="Wczytywanie klienta" className="flex flex-col gap-3">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-4 w-96 max-w-full" />
        <Skeleton className="mt-6 h-9 w-full" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      </div>
    </Page>
  );
}

function ClientProfileView({ client }: { client: ClientDto }) {
  const access = useAccess();
  useRememberRecent({
    type: 'client',
    id: client.id,
    title: client.name,
    subtitle: 'klient',
    to: `/klienci/${client.id}`,
  });
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);

  const archived = client.status === 'archived' || client.archivedAt !== null;
  const canEdit = client.canEdit && access.can('clients', 'edit');
  const canCreateTask = access.can('tasks', 'edit') && !archived;
  const canTime = access.can('time', 'edit') && !archived;
  const NewTaskDialog = getClientNewTaskDialog();
  const AddTimeDialog = getClientAddTimeDialog();

  const tab = CLIENT_TABS.find((t) => t.slug === params.get('zakladka')) ?? CLIENT_TABS[0]!;
  const setTab = (key: string) => {
    const next = CLIENT_TABS.find((t) => t.key === key);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (!next || next.key === 'overview') p.delete('zakladka');
        else p.set('zakladka', next.slug);
        return p;
      },
      { replace: true },
    );
  };

  const newTaskButton = (full: boolean): ReactNode =>
    canCreateTask &&
    (NewTaskDialog ? (
      <Button variant="primary" icon={Plus} size={full ? 'l' : 'm'} fullWidth={full} onClick={() => setTaskOpen(true)}>
        Nowe zadanie
      </Button>
    ) : (
      <Button asChild variant="primary" size={full ? 'l' : 'm'} fullWidth={full}>
        <Link to={`/zadania?nowe=1&klient=${client.id}`}>
          <Plus size={16} strokeWidth={1.75} aria-hidden />
          Nowe zadanie
        </Link>
      </Button>
    ));

  const timeButton = (full: boolean): ReactNode =>
    canTime &&
    (AddTimeDialog ? (
      <Button
        variant="secondary"
        icon={Clock}
        size={full ? 'l' : 'm'}
        fullWidth={full}
        onClick={() => setTimeOpen(true)}
      >
        Dodaj czas
      </Button>
    ) : (
      <Button asChild variant="secondary" size={full ? 'l' : 'm'} fullWidth={full}>
        <Link to="/czas?nowy=1">
          <Clock size={16} strokeWidth={1.75} aria-hidden />
          Dodaj czas
        </Link>
      </Button>
    ));

  const moreMenu = (canEdit || !client.partial) && (
    <Menu>
      <MenuTrigger asChild>
        <IconButton icon={MoreHorizontal} label="Więcej akcji" size={32} iconSize={18} className="max-md:size-11" />
      </MenuTrigger>
      <MenuContent align="end">
        {canEdit && !archived && (
          <MenuItem icon={Pencil} onSelect={() => setEditOpen(true)}>
            Edytuj dane
          </MenuItem>
        )}
        {canEdit && !archived && isValidNip(client.company.nip) && (
          <MenuItem icon={RefreshCw} onSelect={() => setRegistryOpen(true)}>
            Odśwież dane z rejestru
          </MenuItem>
        )}
        {!client.partial && (
          <MenuItem icon={Settings} onSelect={() => void navigate(`/klienci/${client.id}/ustawienia`)}>
            Ustawienia klienta
          </MenuItem>
        )}
        {canEdit && (
          <>
            <MenuSeparator />
            <MenuItem icon={archived ? ArchiveRestore : Archive} onSelect={() => setArchiveOpen(true)}>
              {archived ? 'Przywróć z archiwum' : 'Przenieś do archiwum'}
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );

  const openTasks = client.stats.openTasks;
  const tabItems = CLIENT_TABS.map((t) => ({
    value: t.key,
    label: t.label,
    ...(t.key === 'tasks' && openTasks ? { count: openTasks } : {}),
  }));

  return (
    <Page className="pb-36 md:pb-16">
      <BackLink mobileAction={moreMenu} />
      <PageHeader
        detail
        title={client.name}
        meta={
          <span className="text-fg-muted">
            <span className="hidden md:inline">{metaParts(client, false).join(' · ')}</span>
            <span className="md:hidden">{metaParts(client, true).join(' · ')}</span>
          </span>
        }
        actions={
          <div className="hidden items-center gap-2 md:flex">
            {timeButton(false)}
            {newTaskButton(false)}
            {moreMenu}
          </div>
        }
      />

      {(archived || client.partial || client.openConflicts > 0) && (
        <div className="mt-5 flex flex-col gap-3">
          {archived && (
            <ContextBanner tone="info">
              Klient jest w archiwum: nie pojawia się na listach ani w wyborze klienta.
              {canEdit && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => setArchiveOpen(true)}
                    className="rounded-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                  >
                    Przywróć
                  </button>
                </>
              )}
            </ContextBanner>
          )}
          {client.partial && (
            <ContextBanner tone="info">
              Widzisz kartę klienta i kontakty tylko do odczytu: masz dostęp do wybranych projektów albo zasobów, bez
              umów, kwot i pozostałych danych.
            </ContextBanner>
          )}
          {client.openConflicts > 0 && (
            <ContextBanner tone="warning" linkTo={`/klienci/${client.id}/ustawienia`} linkLabel="Rozstrzygnij">
              {client.openConflicts} {plural(client.openConflicts, ['kontakt ma', 'kontakty mają', 'kontaktów ma'])} ten
              sam email co kontakt innego klienta.
            </ContextBanner>
          )}
        </div>
      )}

      <Tabs
        value={tab.key}
        onValueChange={setTab}
        items={tabItems}
        aria-label="Sekcje klienta"
        className="md:mt-5 [&>[role=tablist]]:hidden md:[&>[role=tablist]]:flex"
      >
        {CLIENT_TABS.map((t) => (
          <TabPanel key={t.key} value={t.key} className="pt-4 md:pt-5">
            <ClientTab tab={t.key} client={client} />
          </TabPanel>
        ))}
      </Tabs>

      {/* Mobile (Figma 05 Klienci mobile): akcje przypięte nad dolnym menu */}
      {(canTime || canCreateTask) && (
        <div className="fos-mobile-actionbar fixed inset-x-0 bottom-[calc(76px+env(safe-area-inset-bottom))] z-30 grid grid-cols-2 gap-2 border-t border-line bg-canvas px-5 py-3 md:hidden">
          {timeButton(true) || <span />}
          {newTaskButton(true)}
        </div>
      )}

      {canEdit && <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} />}
      {canEdit && <RegistryRefreshDialog open={registryOpen} onOpenChange={setRegistryOpen} client={client} />}
      {canEdit && (
        <ArchiveClientDialog
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          client={client}
          mode={archived ? 'restore' : 'archive'}
        />
      )}
      {NewTaskDialog && canCreateTask && <NewTaskDialog client={client} open={taskOpen} onOpenChange={setTaskOpen} />}
      {AddTimeDialog && canTime && <AddTimeDialog client={client} open={timeOpen} onOpenChange={setTimeOpen} />}
    </Page>
  );
}

function ClientTab({ tab, client }: { tab: ClientTabKey; client: ClientDto }) {
  const Registered = getClientTab(tab);
  if (Registered) return <Registered client={client} />;
  if (tab === 'overview') return <ClientOverview client={client} />;
  return <ClientTabPlaceholder tab={tab} />;
}
